import { Outlet, useLocation, useMatches } from 'react-router'
import { useClientSession } from '@/features/account/api/clientProfile.ts'
import { useStudioRole } from '@/app/useStudioRole.ts'

import { AppHeader } from '@/components/layout/AppHeader.tsx'
import { AppFooter } from '@/components/layout/AppFooter.tsx'
import { BottomNav } from '@/components/layout/BottomNav.tsx'
import { useBusinessProfile } from '@/features/business/api/useBusinessProfile.ts'

/**
 * Application shell: header, scrollable content, bottom navigation.
 *
 * The header waits for the studio's real profile rather than borrowing a
 * fixture's name. Showing one studio's name above another's content is the
 * kind of mistake nobody notices until a customer calls the wrong number.
 */
/**
 * Screens that use the full desktop width mark their route `handle: { layout:
 * 'wide' }` — the home and the service list, which lay out in columns. Reading
 * screens stay at a comfortable measure in the middle.
 */
function useWideLayout(): boolean {
  return useMatches().some(
    (match) => (match.handle as { layout?: string } | undefined)?.layout === 'wide',
  )
}

export function RootLayout() {
  const location = useLocation()
  const { business } = useBusinessProfile()
  const wide = useWideLayout()
  const { isOwner } = useStudioRole()
  const { session } = useClientSession()

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      {business === null ? (
        <header className="safe-top mx-auto w-full max-w-5xl px-4 sm:px-6">
          <div className="flex h-20 items-center border-b border-border pt-2">
            <span className="block h-4 w-40 rounded bg-muted" aria-hidden="true" />
          </div>
        </header>
      ) : (
        <AppHeader business={business} isOwner={isOwner} email={session?.email ?? ''} />
      )}

      <main
        className={`mx-auto w-full flex-1 px-4 pt-5 pb-8 sm:px-6 ${wide ? 'max-w-5xl' : 'max-w-2xl'}`}
      >
        {/* Keyed by path so each screen rises in once; off under reduced motion. */}
        <div key={location.pathname} className="screen-enter">
          <Outlet />
        </div>
      </main>

      {business !== null && <AppFooter business={business} isOwner={isOwner} />}

      <BottomNav isOwner={isOwner} />
    </div>
  )
}
